import { jsx, jsxs } from 'react/jsx-runtime';
import { useState, useContext, useEffect } from 'react';
import { InputAdornment, IconButton, Box, Typography, TextField, Button } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useValidate } from '@plumeuk/use-validate';
import { IconContext, NotificationContext, AuthContext, INotificationSeverity } from '@plumeuk/shapeshift-identity';
import 'x2js';

const replaceAll = (str, find, replace) => {
    // Escape special characters in `find` using a regular expression
    const escapedFind = find.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    // Create a regular expression with global flag to replace all occurrences
    const regex = new RegExp(escapedFind, "g");
    // Use the replace method with the regular expression
    return (str ?? "").replace(regex, replace);
};

const useStyles$1 = makeStyles()((_theme, { hasError, valid }) => ({
    inputText: {},
    input: {
        border: valid ? "1px solid #d1d1d1" : "1px solid red",
        padding: "10px 20px",
        marginTop: "8px",
        width: "100%",
        boxSizing: "border-box"
    },
    label: {},
    errorMessage: {
        maxHeight: hasError ? 50 : 0,
        color: "#f30000",
        fontSize: "16px",
        boxSizing: "border-box",
        transition: "max-height 0.15s ease-out",
        "& p": {
            paddingTop: "5px"
        }
    }
}));
const InputText = ({ className, label, value, containerProps, error, onValidate, ...props }) => {
    const [showPassword, setShowPassword] = useState(false);
    const valid = error === undefined;
    const { classes, cx } = useStyles$1({ hasError: !!error, valid });
    const { visibilityIcon, visibilityOffIcon } = useContext(IconContext);
    useEffect(() => {
        if (error !== undefined) {
            onValidate?.();
        }
    }, [value]);
    const inputProps = {
        disableUnderline: true,
        autoCapitalize: "none",
        autoComplete: "off"
    };
    if (props.type === "password") {
        inputProps["endAdornment"] = (jsx(InputAdornment, { position: "end", children: jsx(IconButton, { "data-test-id": "input-text-show-password-button", onClick: () => setShowPassword((prev) => !prev), edge: "end", children: showPassword ? visibilityIcon : visibilityOffIcon }) }));
    }
    return (jsxs(Box, { className: cx(classes.inputText, className), ...containerProps, children: [label && jsx(Typography, { className: classes.label, children: label }), jsx(TextField, { className: classes.input, id: "input-text", variant: "standard", autoComplete: 'off', InputProps: { ...inputProps, ...props.InputProps }, value: value, inputProps: {
                    "data-test-id": "text-input-" + (label ? replaceAll(label?.toLocaleLowerCase(), " ", "-") : ""),
                    autoCapitalize: "none",
                    autoCorrect: "off"
                }, onBlur: e => {
                    props?.onBlur?.(e);
                    onValidate?.();
                }, ...props, type: props.type === "password" && showPassword ? "text" : props.type }), jsx("div", { className: classes.errorMessage, children: jsx("p", { children: error ?? " " }) })] }));
};

const TUserRegister = {
    firstname: "",
    lastname: "",
    email: "",
    password: "",
    username: ""
};

const registerValidationDoc = {
    firstname: [
        "required"
    ],
    lastname: [
        "required"
    ],
    email: [
        "required",
        "email"
    ],
    username: [
        "required"
    ],
    password: [
        "required",
        "password"
    ],
    confirmPassword: [
        "required",
        {
            validate: (e) => e.confirmPassword === e.password,
            errorMessage: "Passwords must match!"
        }
    ]
};

/* eslint-disable no-console */
console.log.bind(null) ;
console.warn.bind(null) ;
const DevError = console.error.bind(null) ;

const useStyles = makeStyles()((theme) => ({
    loginForm: {
        maxWidth: "400px",
        borderRadius: "10px",
        backgroundColor: theme.palette.background.paper,
        padding: "60px 80px",
        [theme.breakpoints.down("sm")]: {
            padding: "20px"
        }
    },
    title: {
        marginBottom: "45px",
        display: "block",
        textAlign: "center",
        width: "100%",
        [theme.breakpoints.down("sm")]: {
            fontSize: "32px",
            lineHeight: "45px",
            padding: "0 30px",
            boxSizing: "border-box"
        }
    },
    input: {
        marginTop: "10px"
    },
    buttonContainer: {
        marginTop: "40px",
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        "& button": {
            width: "100%",
            height: "51px",
            [theme.breakpoints.down("sm")]: {
                fontSize: "15px"
            }
        }
    }
}));
const submitBtnDataTestId = "register-form-submit-btn";
const RegisterForm = ({ className, data, title, token, onRegister, onChange, componentOverrides, ...props }) => {
    const { classes, cx } = useStyles();
    const { notify } = useContext(NotificationContext);
    const [formData, setFormData] = useState(data ?? TUserRegister);
    const [validationResult, runValidate, hasErrors, clearValidation] = useValidate(registerValidationDoc, formData);
    const [postRequestValidation, setPostRequestValidation] = useState({});
    const { registerUser } = useContext(AuthContext);
    useEffect(() => {
        if (data && formData && JSON.stringify(formData) !== JSON.stringify(data))
            onChange?.(formData);
    }, [formData]);
    useEffect(() => {
        if (data)
            setFormData?.(data);
    }, [data]);
    const handleSubmit = async (data) => {
        setPostRequestValidation({});
        if (!runValidate())
            return;
        if (token)
            formData.token = token;
        registerUser(formData, data?.additionalRegisterPayload)
            .then(registerResponse => {
            onRegister?.(registerResponse);
        })
            .catch((e) => {
            DevError(e);
            if (e.response?.status === 403)
                notify("Account creation not permitted", "uh oh", INotificationSeverity.error, 30000);
            if (e.response?.status === 400) {
                const message = e.response.data?.error?.message;
                if (message === "Email or Username are already taken") {
                    setPostRequestValidation({ email: "", username: message });
                }
                else if (message === "Email must be a valid email") {
                    setPostRequestValidation({ email: "" });
                }
                else
                    notify("Please try again", "Something went wrong", INotificationSeverity.error, 5000);
            }
        });
    };
    useEffect(() => {
        setFormData(TUserRegister);
        setPostRequestValidation({});
        clearValidation();
    }, [TUserRegister]);
    const input = (label, prop, type) => (jsx(InputText, { "data-test-id": `register-form-input-${replaceAll(label?.toLocaleLowerCase(), " ", "-")}`, className: classes.input, label: label, type: type, autoCapitalize: "off", value: formData[prop] ?? "", error: validationResult[prop] ?? postRequestValidation[prop], onChange: e => setFormData(prev => ({ ...prev, [prop]: e.target.value })), onValidate: () => runValidate(prop) }));
    return (jsxs(Box, { className: cx(classes.loginForm, className), ...props, children: [componentOverrides?.title ?? jsx(Typography, { variant: "h2", className: classes.title, children: title }), input("Username", "username", "text"), input("Email", "email", "text"), input("Password", "password", "password"), input("Confirm Password", "confirmPassword", "password"), input("First name", "firstname", "text"), input("Last name", "lastname", "text"), componentOverrides?.content?.({ formData, setFormData }), jsx(Box, { className: classes.buttonContainer, children: componentOverrides?.submitButton?.({ disabled: hasErrors, onClick: handleSubmit, "data-test-id": submitBtnDataTestId }) ?? jsx(Button, { "data-test-id": submitBtnDataTestId, disabled: hasErrors, onClick: () => handleSubmit(), children: "Register" }) }), componentOverrides?.footer] }));
};

export { RegisterForm };
